import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ConfirmModalComponent } from "@/components/ConfirmModal/ConfirmModal.component";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { ShareModalComponent } from "@/components/ShareModal/ShareModal.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import {
    APIStatus,
    createWishlist,
    move,
    remove,
    rename,
    selectFirstList
} from "@/redux/modules/api/data/wishlist/wishlist.module";
import {
    canRemoveList,
    getListWithId,
    getWishlists,
    isLoading
} from "@/redux/modules/api/data/wishlist/wishlist.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getCurrentListId } from "@/redux/modules/value/wishlistSidebar/wishlistSidebar.selectors";
import { Config } from "@/utils/config.utils";

import { WishlistDetailComponent } from "./WishlistDetail.component";
import { WishlistItemMoveModalContainer } from "./WishlistItemMoveModal/WishlistItemMoveModal.container";

const WishlistDetailContainer = () => {
    const dispatch = useThunkDispatch();
    const { t } = useGranite();

    const [isShareModalShown, showShareDialog] = useState(false);
    const [isConfirmModalShown, showConfirmRemove] = useState(false);
    const [isMoveModalShown, showMoveDialog] = useState(false);
    const [moveSku, setMoveSku] = useState("");
    const [moveCurrentList, setMoveCurrentList] = useState("");

    const customer = useSelector(getCustomer);
    const currentListId = useSelector(getCurrentListId);
    const currentList = useSelector(getListWithId(currentListId));
    const wishlistCount = useSelector(getWishlists).length;
    const canRemove = useSelector(canRemoveList);
    const isCallingWishListAPI = useSelector(isLoading);

    const [{ data, loading }, getProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        { autoCancel: false, manual: true }
    );

    const isLoggedIn = !!(customer && !customer.guest);

    const currentListSkus = useMemo(() => {
        return currentList?.products.map(({ sku }) => sku);
    }, [currentList]);
    const productSkus = useMemo(() => {
        return data?.map((p) => p.sku);
    }, [data]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(CartModule.load());
    }, []);

    useEffect(() => {
        if (!currentListSkus?.length) {
            return;
        }

        // Check if the products in the current wishlist are already loaded to avoid unnecessary API calls
        if (currentListSkus?.every((sku) => productSkus?.includes(sku))) {
            return;
        }

        getProducts({
            params: {
                sku: currentListSkus?.join(","),
                fields: ProductFieldSet.ProductTile
            }
        });
    }, [currentListSkus, productSkus, getProducts]);

    const onRemove = async () => {
        if (!currentList) return;

        const { type } = await dispatch(remove(currentList.id));

        if (type === APIStatus.SUCCESS) {
            dispatch(selectFirstList);
        }
    };

    const onRename = async (newName: string) => {
        if (!currentList) return;

        return await dispatch(rename(currentList.id, newName));
    };

    const onMove = async (targetListId?: string, newListName?: string) => {
        showMoveDialog(false);

        if (targetListId === currentListId) return;

        if (targetListId) {
            return await dispatch(move(moveSku, currentListId!, targetListId!));
        }

        if (newListName) {
            const wishlist = await dispatch(createWishlist(newListName));
            return await dispatch(
                move(moveSku, currentListId!, wishlist.payload.id)
            );
        }
    };

    const handleRemove = () => {
        showConfirmRemove(true);
    };

    const handleShare = () => {
        showShareDialog(true);
    };

    const handleMove = (sku: string, currentWishlistId: string) => {
        setMoveSku(sku);
        setMoveCurrentList(currentWishlistId);
        showMoveDialog(true);
    };

    const handleOnWishlistItemMoveModalClose = () => {
        showMoveDialog(false);
    };

    if (loading || isCallingWishListAPI) {
        return <Spinner />;
    }

    return (
        <>
            {currentList && (
                <WishlistDetailComponent
                    list={currentList}
                    products={(data || []).filter((prod) =>
                        currentList?.products.some((p) => p.sku === prod.sku)
                    )}
                    canRemove={canRemove}
                    onRemove={handleRemove}
                    onRename={onRename}
                    onShare={handleShare}
                    onMove={handleMove}
                    isLoggedIn={isLoggedIn}
                    canMove={wishlistCount > 1}
                />
            )}
            <ShareModalComponent
                open={isShareModalShown}
                onClose={() => showShareDialog(false)}
                value={`${Config.pages["shared-wishlist"]}?id=${currentList?.id}`}
            />
            <ConfirmModalComponent
                open={isConfirmModalShown}
                onClose={() => showConfirmRemove(false)}
                onConfirm={onRemove}
                confirmButtonText={t("wishlist.detail.modal.confirm.button")}
                text={t("wishlist.detail.modal.confirm.text")}
            />
            <WishlistItemMoveModalContainer
                open={isMoveModalShown}
                currentWishlistId={moveCurrentList}
                onMove={onMove}
                onClose={handleOnWishlistItemMoveModalClose}
            />
        </>
    );
};

export { WishlistDetailContainer };
